import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Paper, Box, Grid, Typography } from '@mui/material'
import { Formik } from 'formik'
import {
  type AssetProjectCreateDto, validateAssetProjectCreateDto
} from '../../../api-schema-types/assets/AssetProjectCrudDtos'
import { useCreateProjectMutation } from '../../../api-services/assets/projectApi'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../app/hooks'
import ButtonLoadingComponent from '../../../views/components/shared/ButtonLoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import FormikTextField from '../../components/ui-widgets/FormikTextField'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'

const CreateSolarProjectPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  // ================================================================================================================
  //  FORM SETUP
  // ================================================================================================================

  const [apiCreateProjectTrigger, createResult] = useCreateProjectMutation()

  const createProjectOnSubmit = async (values: AssetProjectCreateDto): Promise<void> => {
    await apiCreateProjectTrigger({ ...values })
  }

  const newProject: AssetProjectCreateDto = {
    identifier: {
      name: '',
      displayName: '',
      urlSlug: '',
      projectCode: '',
      hubspotDealId: ''
    }
  }

  // ================================================================================================================
  //  FORM MUTATION + RESULT
  // ================================================================================================================

  useEffect(() => {
    if (createResult.isSuccess) {
      dispatch(setNotification({ open: true, severity: 'success', message: `Project ${createResult.data.projectName} created successfully` }))
      history.push(`/app/projects/${createResult.data.projectUrlSlug}/edit`)
    }

    if (createResult.isError) {
      dispatch(setNotification({
        open: true,
        severity: 'error',
        message: RtkQueryHelper.mapResponseError(createResult.error)
      }))
    }
  }, [createResult.isSuccess, createResult.isError])

  // ================================================================================================================
  //  UI Handlers
  // ================================================================================================================

  const handleCancel = async (): Promise<void> => {
    dispatch(setNotification({
      open: true,
      severity: 'warning',
      message: 'Create action cancelled. '
    }))

    history.push('/app/projects')
  }

  return (
    <>
      <Grid container>
        <Grid item lg={12}>
          <Grid container sx={{ mb: 4 }}>
            <Grid item lg={6}>
              <Typography variant={'h4'}>Create Project</Typography>
            </Grid>
            <Grid item lg={6} style={{ textAlign: 'right' }}>
              <Button variant={'outlined'} onClick={() => history.push('/app/projects')} startIcon={<ArrowBackIcon/>}>
                Projects
              </Button>
            </Grid>
          </Grid>

          <Paper>
            <Box sx={{ p: 4, mt: 3 }}>

              <Formik
                initialValues={newProject}
                validationSchema={validateAssetProjectCreateDto}
                onSubmit={async (values, actions) => {
                  await createProjectOnSubmit(values)
                }}
              >
                {formikProps => (

                  <form
                    autoComplete={'off'}
                    autoCorrect={'off'}
                    onSubmit={formikProps.handleSubmit}
                  >

                    {!formikProps.isValid && (
                      <Grid item md={12} sx={{ mt: 3 }}>
                        <ErrorComponent title={'Form validation error'} message={JSON.stringify(formikProps.errors)}/>
                      </Grid>
                    )}

                    <Box sx={{ pb: 4 }}>
                      <h3>Identifier</h3>

                      <Grid container spacing={4}>

                        {/* ==================================================================================================== */}

                        <Grid item md={6}>
                          <FormikTextField formikProps={formikProps} fieldName={'identifier.name'}
                                           label={'Project Name'} type={'text'} locked={false}/>
                        </Grid>

                        <Grid item md={6}>
                          <FormikTextField formikProps={formikProps} fieldName={'identifier.displayName'}
                                           label={'Display Name'} type={'text'} locked={false}/>
                        </Grid>

                        {/* ==================================================================================================== */}

                        <Grid item md={3}>
                          <FormikTextField formikProps={formikProps} fieldName={'identifier.urlSlug'}
                                           label={'Project Url Slug'} type={'text'} locked={false}/>
                        </Grid>

                        <Grid item md={3}>
                          <FormikTextField formikProps={formikProps} fieldName={'identifier.projectCode'}
                                           label={'Project Code'} type={'text'} locked={false}/>
                        </Grid>

                        <Grid item md={3}>
                          <FormikTextField formikProps={formikProps} fieldName={'identifier.hubspotDealId'}
                                           label={'Hubspot Deal ID'} type={'text'} locked={false}/>
                        </Grid>

                        {/* ==================================================================================================== */}

                        <Grid item lg={9} sx={{ mt: 3 }}>
                          <Button variant={'contained'} disabled={!formikProps.isValid && !formikProps.isSubmitting}
                                  type={'submit'} sx={{ mr: 3 }}>
                            Create Project
                            {formikProps.isSubmitting && <ButtonLoadingComponent/>}
                          </Button>
                          <Button type={'button'} variant={'text'} onClick={handleCancel}>
                            Cancel
                          </Button>
                        </Grid>

                      </Grid>

                    </Box>
                  </form>

                )}
              </Formik>

            </Box>
          </Paper>

        </Grid>
      </Grid>

    </>
  )
}

export default CreateSolarProjectPage
